import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "fpfp_favorites";

const readFavorites = (): string[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
};

export const useFavorites = () => {
  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    setFavorites(readFavorites());
    const sync = () => setFavorites(readFavorites());
    window.addEventListener("fpfp-favorites-updated", sync);
    return () => window.removeEventListener("fpfp-favorites-updated", sync);
  }, []);

  const persist = useCallback((next: string[]) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      window.dispatchEvent(new Event("fpfp-favorites-updated"));
    } catch {
      // storage unavailable, keep in-memory state only
    }
  }, []);

  const toggleFavorite = useCallback(
    (id: string): boolean => {
      const current = readFavorites();
      const exists = current.includes(id);
      const next = exists ? current.filter((item) => item !== id) : [...current, id];
      persist(next);
      setFavorites(next);
      return !exists;
    },
    [persist],
  );

  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites]);

  return { favorites, toggleFavorite, isFavorite };
};